import instagramIcon from "../../assets/icon/instagram.svg";
import tiktiokIcon from "../../assets/icon/tiktok.svg";
import facebookIcon from "../../assets/icon/facebook.svg";
import emailIcon from "../../assets/icon/email.svg";
import telphoneIcon from "../../assets/icon/telphone.svg";
import whatsAppIcon from "../../assets/icon/WhatsApp.svg";
import shopeeIcon from "../../assets/icon/shopee.svg";
import tokopediaIcon from "../../assets/icon/tokopedia.svg";
import { useMemo } from "react";
import { useHomePage } from "../../context/HomePageContext";
import { useLanguage } from "../../context/LanguageContext";
import { resolveAchievements, resolveFooter } from "../../lib/homePageContent";
import {
  toInstagramHref,
  toTelHref,
  toWhatsappHref,
} from "../../lib/contactLinks";

// ----------------------------------------------------------------------------
// Telepon, WhatsApp, email, Instagram, alamat, dan angka pencapaian dikelola
// dari admin (menu Home Page -> Footer & Contact / Pencapaian Industri).
// Yang di bawah ini BELUM ada di CMS -> ganti manual sesuai akun resmi.
// ----------------------------------------------------------------------------
const CONTACT = {
  tiktok: "https://tiktok.com/@arunikamedinapratama", // ganti sesuai akun asli
  facebook: "https://facebook.com/arunikamedinapratama", // ganti sesuai akun asli
  shopee: "https://shopee.co.id/arunikamedinapratama", // ganti sesuai toko asli
  tokopedia: "https://tokopedia.com/arunikamedinapratama", // ganti sesuai toko asli
};

// Kelas Tailwind harus tertulis utuh agar ikut ter-generate.
const STATS_GRID_COLUMNS: Record<number, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
};

interface IconCircleProps {
  icon: string;
  alt: string;
  href: string;
  external?: boolean;
}

// Diekstrak dari pattern yang sebelumnya di-copy-paste 6x identik persis
// (cuma icon-nya beda). Sekarang jadi 1 komponen reusable, dan yang lebih
// penting: beneran bisa diklik (sebelumnya cuma <div>, dead UI).
function IconCircle({ icon, alt, href, external = true }: IconCircleProps) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      aria-label={alt}
      className="
    flex-none

    w-12
    h-12
    min-w-12
    min-h-12

    flex
    items-center
    justify-center

    rounded-full
    bg-[#D8EEF0]

    transition-all
    duration-200

    hover:scale-110
    hover:bg-white
  ">
      <img
        src={icon}
        alt=""
        className="
      w-5
      h-5
      object-contain
    "
      />
    </a>
  );
}

export default function FooterSection() {
  const { data, status } = useHomePage();
  const { language } = useLanguage();
  const footer = useMemo(() => resolveFooter(data?.footer), [data]);
  const achievements = useMemo(
    () => resolveAchievements(data?.achievements, language),
    [data, language],
  );

  // Footer ada di bawah halaman: menunggu data sebentar lebih baik daripada
  // menampilkan kontak/angka bawaan lalu berganti.
  if (status === "loading") return null;

  return (
    <footer
      className="
        w-full
relative
        
 z-20
      
      ">
      <div
        className="
          w-full
          

          mx-auto

          bg-[#324A5A]

          rounded-t-[60px]

          px-8
          py-14

          lg:px-14
          lg:py-16
        ">
        {/* TITLE */}
        {achievements.title && (
          <h2
            className="
              text-center
              text-white

              text-[32px]
              font-bold

              mb-10
            ">
            {achievements.title}
          </h2>
        )}

        {/* TOP STATS */}
        <div
          className={`
            grid
            grid-cols-1

            gap-10

            text-center

            ${
              STATS_GRID_COLUMNS[Math.min(achievements.items.length, 4)] ??
              STATS_GRID_COLUMNS[3]
            }
          `}>
          {/* ITEM */}
          {achievements.items.map((item) => (
            <div key={item.key}>
              <h2
                className="
                  text-white

                  text-[40px]
                  font-bold

                  mb-3
                ">
                {item.value}
              </h2>

              <p
                className="
                  text-white/90

                  text-[16px]
                  font-medium
                ">
                {item.label}
              </p>
            </div>
          ))}
        </div>

        {/* LINE */}
        <div
          className="
            w-full
            h-[2px]

            bg-[#6ABEC5]

            my-12
          "
        />

        {/* BOTTOM */}
        <div
          className="
            grid
            grid-cols-1

            gap-12

            md:grid-cols-2

            lg:grid-cols-5
          ">
          {/* BRAND */}
          <div>
            {/* LOGO */}

            <img
              src={footer.logo}
              alt="AMP Logo"
              className="
            

                object-contain
              "
            />
          </div>

          {/* ADDRESS */}
          <div>
            <h3
              className="
                text-white

                text-[20px]
                font-semibold

                mb-5
              ">
              Head Office
            </h3>

            <p
              className="
                whitespace-pre-line
                text-white/90

                text-[16px]
                leading-[30px]
              ">
              {footer.headOffice}
            </p>
          </div>

          {/* ADDRESS */}
          <div>
            <h3
              className="
                text-white

                text-[20px]
                font-semibold

                mb-5
              ">
              Workshop
            </h3>

            <p
              className="
                whitespace-pre-line
                text-white/90

                text-[16px]
                leading-[30px]
              ">
              {footer.workshop}
            </p>
          </div>

          {/* CONTACT */}
          <div>
            <h3
              className="
                text-white

                text-[20px]
                font-semibold

                mb-5
              ">
              Contact Us
            </h3>

            <div
              className="
              flex
              flex-wrap
              items-center

              gap-4
            ">
              {footer.phone && (
                <IconCircle
                  icon={telphoneIcon}
                  alt="Telepon"
                  href={toTelHref(footer.phone)}
                  external={false}
                />
              )}

              {footer.whatsapp && (
                <IconCircle
                  icon={whatsAppIcon}
                  alt="WhatsApp"
                  href={toWhatsappHref(footer.whatsapp)}
                />
              )}

              {footer.email && (
                <IconCircle
                  icon={emailIcon}
                  alt="Email"
                  href={`mailto:${footer.email}`}
                  external={false}
                />
              )}

              {footer.instagram && (
                <IconCircle
                  icon={instagramIcon}
                  alt="Instagram"
                  href={toInstagramHref(footer.instagram)}
                />
              )}

              {/* <IconCircle
                icon={tiktiokIcon}
                alt="TikTok"
                href={CONTACT.tiktok}
              /> */}

              {/* <IconCircle
                icon={facebookIcon}
                alt="Facebook"
                href={CONTACT.facebook}
              /> */}
            </div>
          </div>

          {/* MARKETPLACE 
          <div>
            <h3
              className="
                text-white

                text-[20px]
                font-semibold

                mb-5
              ">
              Marketplace
            </h3>

            <div
              className="
                flex
                items-center

                gap-4
              ">
              <IconCircle
                icon={shopeeIcon}
                alt="Shopee"
                href={CONTACT.shopee}
              />

              <IconCircle
                icon={tokopediaIcon}
                alt="Tokopedia"
                href={CONTACT.tokopedia}
              />
            </div> 
          </div>*/}
        </div>
      </div>
    </footer>
  );
}
